/**
 * Single-song player — /songs/$songId
 *
 * Two modes on the same stage:
 * - Listen: autoplay with the key highlighted (visual guide).
 * - Your turn: the child plays; the expected key stays highlighted and the
 *   song advances on a correct note (no penalty for mistakes).
 */

import { createEffect, createMemo, createResource, createSignal, onCleanup, Show } from "solid-js";
import { createRoute, Link, useNavigate, useParams, useSearch } from "@tanstack/solid-router";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Hand,
  Play,
  RotateCcw,
  Square,
  Star,
} from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import {
  createProgressStore,
  createSongPlayer,
  getSong,
  type SongPlayer,
  SONGS,
  songStickerId,
  type SoundKey,
} from "@melodimals/game-core";
import { rootRoute } from "@/routes/__root";
import { useI18n } from "@/providers/i18n-provider";
import { useAudio } from "@/providers/audio-provider";
import { acquireWakeLock } from "@/lib/kid-friendly";
import { createGestureRecognizer } from "@/lib/gestures/recognizer";
import { pointerHandlers } from "@/lib/gestures/solid";
import { SONG_CAROUSEL } from "@/lib/gestures/presets";
import { vibrate } from "@/lib/gestures/haptics";
import { InstrumentStage } from "@/features/instruments/instrument-stage";
import { RewardPopup } from "@/features/rewards/reward-popup";
import { useRewards } from "@/features/rewards/use-rewards";
import { PlayErrorBoundary } from "@/components/play-error-boundary";
import { NotFoundState } from "@/components/ui/not-found-state";

/** True if the user asked the OS to minimise motion. */
function prefersReducedMotion(): boolean {
  // oxlint-disable-next-line lingui/no-unlocalized-strings -- CSS media query, not UI text
  return globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

function SongStage() {
  const { t } = useI18n();
  const audio = useAudio();
  const progress = createProgressStore();
  const rewards = useRewards();
  const navigate = useNavigate();
  const params = useParams({ from: "/songs/$songId" });
  const search = useSearch({ from: "/songs/$songId" });
  const song = createMemo(() => getSong(params().songId));

  /**
   * URL addressability for song mode (#15/#16): `?mode=listen|play`.
   * This allows sharing a link that opens the song already playing the guide
   * ("Listen") or ready for the child to play ("Your turn"). The default state
   * (song at rest, choosing a mode) has no search param—we omit it to keep URLs
   * clean. Canonical pattern: `validateSearch` + `useSearch` + an idempotent
   * URL→signal effect, and `navigate({replace:true})` to reflect the user's
   * action in the URL without cluttering history.
   */
  const setModeInUrl = (mode: "listen" | "play" | undefined) => {
    void navigate({
      to: "/songs/$songId",
      params: { songId: params().songId },
      search: mode === undefined ? {} : { mode },
      replace: true,
    });
  };

  const [highlighted, setHighlighted] = createSignal<SoundKey | null>(null);
  const [playing, setPlaying] = createSignal(false);
  const [yourTurn, setYourTurn] = createSignal(false);
  const [finished, setFinished] = createSignal(false);
  let player: SongPlayer | null = null;

  // Listen / Your-turn start the SongPlayer on `audio.engine`; without the
  // instrument samples resident, playback would be silent or broken.
  // The stage preloads them asynchronously, so we gate the action buttons
  // until THIS song's instrument is loaded (the same pattern as the
  // mini-games).
  const [instrumentReady] = createResource(
    () => song()?.instrument,
    async (instrument) => {
      await audio.loadInstrument(instrument);
      return true;
    },
  );
  /**
   * Gate at the SOURCE (P23/CC-60, invariants I1/I2).
   *
   * `createResource` is Solid's other SUSPENDING primitive: reading its raw
   * accessor (`instrumentReady()`) during render registers the wait with the
   * nearest `<Suspense>`—and throws if the resource failed. This header
   * does not hang from its own `<Suspense>`, so the boundary that caught it
   * was `__root`'s `<Suspense fallback={null}>`: while the samples downloaded,
   * Solid disconnected the ENTIRE screen from the DOM (measured:
   * `document.body.innerText === ""` throughout the flight, while the song
   * list rendered normally in the same run).
   *
   * `state` and `latest` do not suspend once the resource has resolved,
   * which is exactly the case this gate lets through. `!instrumentReady.loading`
   * would NOT work: it is also `false` on error, and the accessor would throw
   * again.
   */
  const ready = () =>
    (instrumentReady.state === "ready" || instrumentReady.state === "refreshing") &&
    (instrumentReady.latest ?? false);

  const stopPlayer = () => {
    player?.stop();
    player = null;
    setPlaying(false);
    setHighlighted(null);
  };

  /** Manual stop (square button): stops the guide and returns to the idle
   * state, clearing the URL's `?mode` so it reflects "not playing". */
  const stopListening = () => {
    stopPlayer();
    setModeInUrl(undefined);
  };

  onCleanup(stopPlayer);
  // The screen does not turn off halfway through a song.
  onCleanup(acquireWakeLock());

  const startListen = () => {
    const current = song();
    if (!current) {
      return;
    }
    setModeInUrl("listen");
    stopPlayer();
    setYourTurn(false);
    setFinished(false);
    setPlaying(true);
    player = createSongPlayer(audio.engine, current, "listen", {
      onNoteOn: (key) => setHighlighted(key),
      onFinished: () => {
        setPlaying(false);
        setHighlighted(null);
      },
    });
    player.start();
  };

  const startYourTurn = () => {
    const current = song();
    if (!current) {
      return;
    }
    setModeInUrl("play");
    stopPlayer();
    setFinished(false);
    setYourTurn(true);
    player = createSongPlayer(audio.engine, current, "play", {
      onFinished: () => {
        setFinished(true);
        setHighlighted(null);
        // Reward: learned song → persistent sticker + animation if it is new.
        progress.recordSongCompleted(current.id);
        rewards.award(songStickerId(current.id));
      },
    });
    setHighlighted(player.expectedKey());
  };

  // URL→signal effect (deep link): when `?mode` is present and the instrument
  // is loaded, start the corresponding mode. Idempotent—it starts only if
  // that mode is not already running, preventing restarts when the effect
  // reruns (e.g. when `instrumentReady` changes or after swiping to another song).
  // The starters write the same `?mode` with replace, so there is no feedback loop.
  let appliedFor = "";
  createEffect(() => {
    const id = params().songId;
    const { mode } = search();
    if (!ready()) {
      return;
    }
    // Reapply the URL mode once per (song, mode); changing songs
    // (swipe) clears the signals and must reevaluate the new URL deep link.
    const token = `${id}:${mode ?? ""}`;
    if (token === appliedFor) {
      return;
    }
    appliedFor = token;
    if (mode === "listen" && !playing()) {
      startListen();
    } else if (mode === "play" && !yourTurn()) {
      startYourTurn();
    }
  });

  /** In "your turn" mode, each key played is evaluated against the expected one. */
  const handleStageNote = (key: SoundKey) => {
    if (!yourTurn() || !player) {
      return;
    }
    player.handleInput(key);
    setHighlighted(player.expectedKey());
  };

  // --- Swipe-to-next-song (carousel) --------------------------------------
  // The header title strip tracks the finger horizontally; on commit (or fling)
  // the player glides to the neighbouring song and wraps around so the child
  // never hits a dead end. Physics (deadzone, axis-lock, fling) come from the
  // shared recognizer; tuning lives in SONG_CAROUSEL — premium and consistent
  // with the canon gesture standard. Targets are big and tolerant for 2–6yo.
  const currentIndex = createMemo(() => SONGS.findIndex((s) => s.id === params().songId));
  const neighbourId = (delta: number): string | undefined => {
    const i = currentIndex();
    if (i < 0 || SONGS.length < 2) {
      return undefined;
    }
    const next = (i + delta + SONGS.length) % SONGS.length;
    return SONGS[next]?.id;
  };

  const [dragX, setDragX] = createSignal(0);
  const [sliding, setSliding] = createSignal(false);
  let stripRef: HTMLDivElement | undefined;

  const stripWidth = () => stripRef?.clientWidth ?? SONG_CAROUSEL.fallbackWidthPx;

  /** delta>0 = next song (current title slides left). */
  const goToNeighbour = (delta: number) => {
    const id = neighbourId(delta);
    if (id === undefined) {
      setSliding(true);
      setDragX(0);
      return;
    }
    stopPlayer();
    setYourTurn(false);
    setFinished(false);
    if (prefersReducedMotion()) {
      setDragX(0);
      void navigate({ to: "/songs/$songId", params: { songId: id } });
      return;
    }
    const width = stripWidth();
    setSliding(true);
    setDragX(delta > 0 ? -width : width);
    vibrate(SONG_CAROUSEL.hapticMs);
    globalThis.setTimeout(() => {
      setSliding(false);
      setDragX(0);
      void navigate({ to: "/songs/$songId", params: { songId: id } });
    }, SONG_CAROUSEL.transitionMs);
  };

  const snapBack = () => {
    setSliding(true);
    setDragX(0);
  };

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: SONG_CAROUSEL.thresholdPx,
    fling: { ...SONG_CAROUSEL.fling },
    onStart: () => setSliding(false),
    onMove: (sample) => {
      setSliding(false);
      setDragX(sample.dx);
    },
    onEnd: (sample) => {
      const commitPx = Math.min(
        SONG_CAROUSEL.commitThresholdPx,
        stripWidth() * SONG_CAROUSEL.commitThresholdRatio,
      );
      if (sample.isFling || Math.abs(sample.dx) >= commitPx) {
        // Swipe left (dx<0) → next song; swipe right (dx>0) → previous song.
        goToNeighbour(sample.dx < 0 ? 1 : -1);
      } else {
        snapBack();
      }
    },
    onCancel: snapBack,
  });

  const swipeHandlers = pointerHandlers(recognizer);

  const stripTransform = () => `translate3d(${dragX()}px, 0, 0)`;
  const stripTransition = () =>
    sliding() ? `transform ${SONG_CAROUSEL.transitionMs}ms ease-out` : "none";

  return (
    <main class="kid-stage-screen screen-enter bg-gradient-to-b from-violet-100 to-violet-300 p-3">
      <header class="flex items-center gap-3 pb-2">
        <Link
          to="/songs"
          class="press flex h-12 w-12 items-center justify-center rounded-full bg-white/80 shadow"
          aria-label={t(msg`Back`)}
        >
          <ArrowLeft class="h-6 w-6 text-violet-900" />
        </Link>
        {/* Swipeable song strip: drag/fling left or right to glide to the
            neighbouring song. `touch-action: pan-y` lets vertical scroll pass
            through while we claim the horizontal axis. Chevrons hint the
            affordance for the (rare) reader. */}
        <div
          ref={stripRef}
          class="relative flex min-w-0 flex-1 items-center gap-1 overflow-hidden select-none"
          style={{ "touch-action": "pan-y" }}
          aria-roledescription={t(msg`Song carousel`)}
          {...swipeHandlers}
        >
          <Show when={SONGS.length > 1}>
            <ChevronLeft class="h-5 w-5 shrink-0 text-violet-900/40" aria-hidden="true" />
          </Show>
          {/* Ancla del chrome estático de la ruta (P23/I4): tiene que estar en
              pantalla ANTES de que respondan los samples. */}
          <h1
            data-testid="song-chrome-title"
            class="min-w-0 flex-1 truncate text-center text-lg font-bold text-violet-900"
            style={{ transform: stripTransform(), transition: stripTransition() }}
          >
            {song()?.title}
          </h1>
          <Show when={SONGS.length > 1}>
            <ChevronRight class="h-5 w-5 shrink-0 text-violet-900/40" aria-hidden="true" />
          </Show>
        </div>
        <Show
          when={!playing()}
          fallback={
            <button
              type="button"
              onPointerDown={stopListening}
              class="press flex h-12 w-12 items-center justify-center rounded-full bg-white shadow"
              aria-label={t(msg`Stop`)}
            >
              <Square class="h-5 w-5 text-violet-900" />
            </button>
          }
        >
          <button
            type="button"
            onPointerDown={startListen}
            disabled={!ready()}
            class="press flex h-12 w-12 items-center justify-center rounded-full bg-white shadow disabled:opacity-40"
            aria-label={t(msg`Listen`)}
          >
            <Play class="h-6 w-6 text-violet-900" />
          </button>
        </Show>
        <button
          type="button"
          onPointerDown={startYourTurn}
          disabled={!ready()}
          class={`flex h-12 w-12 items-center justify-center rounded-full shadow disabled:opacity-40 ${yourTurn() ? "bg-violet-600 text-white" : "bg-white text-violet-900"}`}
          aria-label={t(msg`Your turn`)}
        >
          <Hand class="h-6 w-6" />
        </button>
      </header>

      <Show when={finished()}>
        <div class="flex items-center justify-center gap-2 pb-2" aria-hidden="true">
          <Star class="h-8 w-8 fill-amber-400 text-amber-500" />
          <Star class="h-10 w-10 fill-amber-400 text-amber-500" />
          <Star class="h-8 w-8 fill-amber-400 text-amber-500" />
          <button
            type="button"
            onPointerDown={startYourTurn}
            class="press ml-2 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow"
          >
            <RotateCcw class="h-5 w-5 text-violet-900" />
          </button>
        </div>
      </Show>

      <div class="min-h-0 flex-1">
        <PlayErrorBoundary scope="songs">
          <Show when={song()} keyed>
            {(current) => (
              <InstrumentStage
                instrument={current.instrument}
                highlightedKey={highlighted()}
                onNotePlayed={handleStageNote}
              />
            )}
          </Show>
        </PlayErrorBoundary>
      </div>

      <RewardPopup stickerId={rewards.pending()} onDismiss={rewards.dismiss} />
    </main>
  );
}

/**
 * Validates `$songId` against the song registry. An unknown ID (broken link
 * / hand-edited URL) → a playful "not found" state with a CTA to the song
 * list, instead of an empty stage with no song. The real stage mounts only
 * with a valid ID (`<Show keyed>` cleanly remounts it when the song changes).
 */
function SongPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const params = useParams({ from: "/songs/$songId" });
  const known = createMemo(() => getSong(params().songId) !== undefined);

  return (
    <Show
      when={known()}
      fallback={
        <main class="kid-screen items-center justify-center bg-background p-6">
          <NotFoundState
            emoji="🎵"
            title={t(msg`We can't find that song`)}
            description={t(msg`This song isn't here. Let's pick another one!`)}
            primaryAction={{
              label: t(msg`See all songs`),
              onClick: () => {
                void navigate({ to: "/songs" });
              },
            }}
          />
        </main>
      }
    >
      <SongStage />
    </Show>
  );
}

/** URL-addressable song mode. The idle state (not playing) is represented by
 * the absence of `mode`, so we omit the default to keep URLs clean. */
interface SongSearch {
  mode?: "listen" | "play";
}

export const songRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/songs/$songId",
  validateSearch: (search: Record<string, unknown>): SongSearch => ({
    mode: search.mode === "listen" || search.mode === "play" ? search.mode : undefined,
  }),
  component: SongPage,
});
